import { useEffect, useRef } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { formatClock } from "@/lib/podcast";

/**
 * Inline, SEO-readable transcript. Renders every caption cue as real text in
 * the DOM (crawlable), inside a fixed-height scrollable frame so it doesn't
 * inflate the page. Cues are tap-to-jump and highlight/auto-scroll in sync
 * with the persistent player.
 */
export default function TranscriptPanel({ ep, active, time, onJump }) {
  const cues = ep.transcriptCues || [];
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  const activeIndex = active
    ? cues.findIndex((c, i) => {
        const n = cues[i + 1];
        return time >= c.start && (!n || time < n.start);
      })
    : -1;

  useEffect(() => {
    if (activeIndex < 0 || !containerRef.current || !activeRef.current) return;
    const c = containerRef.current;
    const el = activeRef.current;
    c.scrollTo({
      top: el.offsetTop - c.clientHeight / 2 + el.clientHeight / 2,
      behavior: "smooth",
    });
  }, [activeIndex]);

  if (!cues.length) return null;

  return (
    <section
      data-testid="episode-transcript"
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16"
    >
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d]">
          Transcript
        </h2>
        {ep.transcriptUrl && (
          <a
            href={ep.transcriptUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="transcript-open-full"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#374566] hover:text-[#9a6700] transition-colors"
          >
            <ExternalLink size={15} /> Open full transcript
          </a>
        )}
      </div>

      <div className="rounded-md border border-[#0b1f4d]/10 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#0b1f4d]/10 bg-[#f3ecdc] text-[#0b1f4d]">
          <FileText size={16} className="text-[#9a6700]" />
          <h3 className="font-mono text-xs uppercase tracking-[0.2em]">
            Full transcript
          </h3>
          <span className="ml-auto font-mono text-[11px] text-[#66718d] hidden sm:inline">
            Tap a line to jump
          </span>
        </div>
        <div
          ref={containerRef}
          data-testid="transcript-scroll"
          className="relative max-h-[26rem] overflow-y-auto p-3 sm:p-4"
        >
          {cues.map((c, i) => {
            const isCurrent = i === activeIndex;
            return (
              <button
                key={i}
                ref={isCurrent ? activeRef : null}
                data-testid={`transcript-cue-${i}`}
                onClick={() => onJump(c.start)}
                aria-label={`Jump to ${formatClock(c.start)}`}
                className={`group w-full text-left flex gap-3 rounded-sm px-3 py-2 transition-colors hover:bg-[#f5b61c]/10 focus-visible:ring-2 focus-visible:ring-[#f5b61c]/50 outline-none ${
                  isCurrent ? "bg-[#f5b61c]/15" : ""
                }`}
              >
                <span
                  className={`font-mono text-xs pt-1 w-14 shrink-0 tabular-nums ${
                    isCurrent
                      ? "text-[#8a5a00] font-semibold"
                      : "text-[#9a6700] group-hover:text-[#8a5a00]"
                  }`}
                >
                  {formatClock(c.start)}
                </span>
                <span
                  className={`text-[15px] leading-relaxed ${
                    isCurrent ? "text-[#0b1f4d] font-medium" : "text-[#374566]"
                  }`}
                >
                  {c.text}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
